import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  Search, 
  Download, 
  Heart, 
  Share2, 
  Image, 
  Users,
  Gift,
  Sparkles
} from 'lucide-react';

const features = [
  {
    icon: Search,
    title: 'Smart Search',
    description: 'Find quotes by author, year, favorites, and quote wall status'
  },
  {
    icon: Download,
    title: 'Easy Export',
    description: 'Download quotes to CSV and Excel formats'
  },
  {
    icon: Share2,
    title: 'Share Quotes',
    description: 'Send selected quotes to the public quote wall'
  },
  {
    icon: Image,
    title: 'Create Cards',
    description: 'Generate beautiful quote cards for social media or personalize printed gifts'
  }
];

export default function OverviewPage() {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container-praise py-8">
        {/* Header */}
        <div className="relative mb-12">
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="absolute left-0 top-0 btn-secondary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </motion.button>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          {/* Early Access Badge */}
          <motion.div variants={itemVariants} className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-full text-brand-600 font-medium">
              <Sparkles className="h-4 w-4" />
              <span>Now in Early Access</span>
            </div>
          </motion.div>

          {/* Hero Section */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-gray-900 mb-6">
              MyGoodQuotes Overview
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              A simple way to capture and preserve meaningful moments through the words of your loved ones.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/early-access')}
                className="btn-primary text-lg px-8 py-3"
              >
                Get Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/demo')}
                className="btn-secondary text-lg px-8 py-3"
              >
                Watch Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </motion.div>

          {/* Features Grid */}
          <motion.div 
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="card p-6"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-brand-50 text-brand-600 mb-4">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Benefits Section */}
          <motion.div variants={itemVariants} className="card p-8 mb-16">
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
              Why Use MyGoodQuotes?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-gray-600">
                  Remembering things you heard is often difficult, and we don't always have paper
                  and pen to write it down. Even when we do, it can get lost. Having all your quotes
                  digitally stored in one place with option to download helps you hold on to those
                  memories longer.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <Users className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                    <span>Build community and create personal connections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Heart className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                    <span>Save cherished moments for generations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Share2 className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                    <span>Share selected quotes with family or on social media</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Gift className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                    <span>Add quotes to personalize everyday products like 
                          <br className="hidden sm:block" />
                      picture frames, t-shirts, greeting/post cards</span>
                  </li>
                </ul>
              </div>
              <div>
                <img
                  src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/list_table_page.jpg"
                  alt="MyGoodQuotes Interface"
                  className="rounded-lg shadow-praise"
                />
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div 
            variants={itemVariants}
            className="card p-8 text-center bg-gradient-to-br from-brand-50 to-white"
          >
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-4">
              Ready to start capturing memories?
            </h2>
            <p className="text-gray-600 mb-8">
              Join our early access program and be one of the first to experience MyGoodQuotes.
            </p>
            <button
              onClick={() => navigate('/early-access')}
              className="btn-primary text-lg px-8 py-3"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}